import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  BASE_SERVER_URL,
  KEYCLOAK_CLIENT_ID,
  POST_DOMAIN_PART
} from 'src/config';
import useAnalytics from 'src/hooks/useAnalytics';
import { useDomainAvailability } from 'src/hooks/useDomainAvailability';
import {
  kcinit,
  removeRealmDomain,
  setClientId,
  setRealmDomain,
  setRealmName,
  setUserEmailAddress
} from 'src/keycloak';
import { setAlert } from 'src/redux/slices/snackbar';
import { useDispatch } from 'src/redux/store';
import type { TenantSignupResponse } from 'src/types/api';
import { ALERT } from 'src/types/enum';
import { SIGNUP_PASSWORD_SET, TENANT_CREATED } from 'src/utils/analyticsUtils';
import axios from 'src/utils/axios';
import { DEFAULT_APPLICATION_ROUTE } from 'src/utils/constants';
import logger from 'src/utils/logger';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';

import type { PasswordSettingProps } from './PasswordSetting';
import type { PasswordSettingFormValues } from './schema';
import {
  PASSWORD_RULES,
  passwordSettingSchema,
  tenantDomainSchema
} from './schema';

export const usePasswordSetting = ({
  state,
  email,
  token
}: PasswordSettingProps) => {
  const {
    t,
    i18n: { language }
  } = useTranslation();
  const collectAnalytics = useAnalytics();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isVerifying, setIsVerifying] = useState(false);
  const [isPasswordEnabled, setIsPasswordEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuggested, setIsSuggested] = useState(false);
  // Domain returned by the verification call, submitted as-is when the user cannot edit it.
  const [verifiedDomain, setVerifiedDomain] = useState('');

  const methods = useForm<PasswordSettingFormValues>({
    resolver: zodResolver(passwordSettingSchema(t, isSuggested)),
    defaultValues: { password: '', confirmPassword: '', tenantDomain: '' },
    mode: 'onChange'
  });

  const {
    clearErrors,
    formState: { errors, isValid },
    handleSubmit,
    setError,
    setValue,
    watch
  } = methods;

  const [password, confirmPassword, tenantDomain] = watch([
    'password',
    'confirmPassword',
    'tenantDomain'
  ]);
  const domainError = errors.tenantDomain?.message ?? '';

  // Gate the lookup on the format rules only — deriving it from `errors` would feed the
  // "unavailable" error back in and reset the very result that produced it.
  const parsedDomain = tenantDomainSchema(t).safeParse(tenantDomain);

  const { domainStatus } = useDomainAvailability({
    email,
    domain: parsedDomain.success
      ? `${parsedDomain.data}.${POST_DOMAIN_PART}`
      : '',
    enabled: isSuggested
  });

  // Availability is not a form value and the schema cannot see it, so the answer is
  // published into the field's error state once it lands. The next keystroke re-runs
  // the resolver, which clears this error on its own.
  useEffect(() => {
    if (!isSuggested) {
      return;
    }
    if (domainStatus === 'taken') {
      setError('tenantDomain', {
        type: 'unavailable',
        message: t(T.domainUnavailable)
      });
    } else if (domainStatus === 'available') {
      clearErrors('tenantDomain');
    }
  }, [domainStatus, isSuggested, setError, clearErrors, t]);

  const passwordCriteria = useMemo(
    () => [
      ...PASSWORD_RULES.map(({ key, isValid }) => ({
        key,
        valid: isValid(password)
      })),
      {
        key: T.passwordRequirementMatch,
        valid: !!confirmPassword && password === confirmPassword
      }
    ],
    [password, confirmPassword]
  );

  const onSubmit = handleSubmit(async (values) => {
    // The resolver only knows the format rules, and handleSubmit has just replaced the
    // errors with its output — so re-assert the availability verdict here. This also
    // covers the window where the lookup has not answered yet.
    if (isSuggested && domainStatus !== 'available') {
      if (domainStatus === 'taken') {
        setError('tenantDomain', {
          type: 'unavailable',
          message: t(T.domainUnavailable)
        });
      }
      return;
    }
    collectAnalytics(SIGNUP_PASSWORD_SET);
    const url = `${BASE_SERVER_URL}/api/v1/tenant/signup`;
    const formData = new FormData();
    formData.append('email', email);
    formData.append('password', values.password);
    formData.append('confirm_password', values.confirmPassword);
    formData.append('language', language);
    formData.append(
      'tenant_domain',
      isSuggested
        ? `${values.tenantDomain}.${POST_DOMAIN_PART}`
        : verifiedDomain
    );
    setIsLoading(true);
    await axios
      .post(url, formData)
      .then((response: TenantSignupResponse) => {
        if (response?.status === 200) {
          collectAnalytics(TENANT_CREATED);
          const realm = response?.data?.ret_val?.realm?.trim() ?? '';
          const userEmail = response?.data?.ret_val?.user_name?.trim() ?? '';
          const tenantDomain =
            response?.data?.ret_val?.tenant_domain?.trim() ?? '';
          setRealmName(realm);
          setUserEmailAddress(userEmail);
          if (tenantDomain) {
            setRealmDomain(tenantDomain);
          } else {
            removeRealmDomain();
          }
          setClientId(KEYCLOAK_CLIENT_ID);
          /* eslint-disable @typescript-eslint/no-unsafe-assignment -- keycloak kcinit is untyped */
          const initPromise = kcinit({
            realm,
            clientId: KEYCLOAK_CLIENT_ID,
            userEmail
          }) as Promise<void>;
          /* eslint-enable @typescript-eslint/no-unsafe-assignment */
          void initPromise
            .then(() => {
              navigate(DEFAULT_APPLICATION_ROUTE);
            })
            .catch((error: unknown) => {
              logger.log(error);
              setIsLoading(false);
            });
        } else {
          setIsLoading(false);
          dispatch(
            setAlert({
              msg: t(T.selfSignUpFailed),
              type: ALERT.ERROR
            })
          );
        }
      })
      .catch((err: { response?: { data?: { msg?: string } } }) => {
        setIsLoading(false);
        if (err?.response?.data?.msg) {
          dispatch(
            setAlert({
              msg: t(T.alreadyHaveAccount),
              type: ALERT.ERROR
            })
          );
        }
      });
  });

  const validateUser = useCallback(async () => {
    const url = `${BASE_SERVER_URL}/api/v1/tenant/verify-email?state=${state?.trim()}&email=${email}&token=${token}`;
    setIsVerifying(true);
    await axios
      .get(url)
      .then(
        (resp: {
          status: number;
          data?: {
            ret_val?: { tenant_domain?: string; is_domain_suggested?: boolean };
          };
        }) => {
          if (resp.status === 200) {
            setIsPasswordEnabled(true);
            const domain = resp?.data?.ret_val?.tenant_domain?.trim() ?? '';
            const suggested = resp?.data?.ret_val?.is_domain_suggested ?? false;
            setVerifiedDomain(domain);
            setIsSuggested(suggested);
            if (suggested) {
              setValue(
                'tenantDomain',
                domain.replace(`.${POST_DOMAIN_PART}`, ''),
                {
                  shouldValidate: true
                }
              );
            }
          }
        }
      )
      .catch(() => {
        setIsPasswordEnabled(false);
      })
      .finally(() => {
        setIsVerifying(false);
      });
  }, [state, email, token, setValue]);

  useEffect(() => {
    void validateUser();
  }, [validateUser]);

  return {
    methods,
    isVerifying,
    isPasswordEnabled,
    isLoading,
    isSuggested,
    domainStatus,
    domainError,
    passwordCriteria,
    onSubmit
  };
};
